import React from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

// Stylized wordmark "logos" (not official brand marks) rendered in the site aesthetic.
const partners = [
  {
    id: "ibm",
    name: "IBM Business Partner",
    descKey: "partner_ibm_desc",
    accent: "#00A3FF",
    mark: (
      <div className="inline-flex items-center rounded-lg bg-white px-3 py-2">
        <img
          src="https://customer-assets.emergentagent.com/job_pm-elite-1/artifacts/4lrksykc_image.png"
          alt="IBM Business Partner"
          className="h-12 w-auto object-contain"
        />
      </div>
    ),
  },
  {
    id: "emergent",
    name: "Emergent",
    descKey: "partner_emergent_desc",
    accent: "#00F0FF",
    mark: (
      <div className="flex items-center gap-2.5">
        <svg width="30" height="30" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5702 8.13142C15.7729 8.0412 16.0007 8.18878 15.9892 8.4103C15.8374 11.3192 14.0965 14.0405 11.2531 15.3065C8.40964 16.5725 5.2224 16.0453 2.95912 14.2117C2.78676 14.072 2.82955 13.804 3.03219 13.7137L4.95677 12.8568C5.04866 12.8159 5.15446 12.823 5.24204 12.8725C6.73377 13.7153 8.59176 13.8649 10.2772 13.1145C11.9626 12.3641 13.0947 10.8833 13.4665 9.21075C13.4883 9.11256 13.5539 9.02918 13.6457 8.98827L15.5702 8.13142Z" fill="#00F0FF"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M15.3066 4.74698L15.5067 5.19653C15.5759 5.35178 15.5061 5.53366 15.3508 5.60278L1.29992 11.8586C1.14467 11.9278 0.962794 11.8579 0.893675 11.7027L0.701732 11.2716L0.693457 11.2531C-1.10317 7.21778 0.711626 2.49007 4.74692 0.693443C8.78221 -1.10318 13.51 0.711693 15.3066 4.74698ZM2.82356 8.55367C2.63552 8.63739 2.41991 8.51617 2.40853 8.31065C2.28373 6.05724 3.53858 3.85787 5.72286 2.88536C7.90715 1.91286 10.3813 2.45199 11.9724 4.05256C12.1175 4.19854 12.0633 4.43988 11.8753 4.5236L2.82356 8.55367Z" fill="#00F0FF"/>
        </svg>
        <span className="font-head font-bold text-2xl text-white tracking-tight">Emergent</span>
      </div>
    ),
  },
  {
    id: "claude",
    name: "Claude Fable 5",
    descKey: "partner_claude_desc",
    accent: "#FF7A45",
    mark: (
      <div className="flex items-center gap-2">
        <span className="w-7 h-7 rounded-full border-2 border-[#FF7A45] flex items-center justify-center">
          <span className="w-3 h-3 rounded-full bg-[#FF7A45]" />
        </span>
        <div className="leading-none">
          <span className="font-head font-bold text-2xl text-white tracking-tight">Claude</span>
          <span className="block font-mono text-[10px] tracking-[0.3em] text-[#FF7A45] uppercase mt-0.5">Fable 5</span>
        </div>
      </div>
    ),
  },
];

export const Partners = () => {
  const { t } = useLang();
  return (
    <section id="partners" className="relative py-24 lg:py-32" data-testid="partners-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-14">
          <div className="text-xs tracking-[0.2em] uppercase font-mono text-cyan mb-4">{t("partners_eyebrow")}</div>
          <h2 className="font-head font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">{t("partners_title")}</h2>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed">{t("partners_subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {partners.map((p, i) => (
            <motion.div
              key={p.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="card-glow rounded-2xl p-8 flex flex-col"
              data-testid={`partner-card-${p.id}`}
              style={{ "--pa": p.accent }}
            >
              <div className="h-16 flex items-center">{p.mark}</div>
              <div className="mt-6 h-px w-full" style={{ background: `linear-gradient(90deg, ${p.accent}55, transparent)` }} />
              <p className="mt-6 text-sm text-zinc-400 leading-relaxed">{t(p.descKey)}</p>
            </motion.div>
          ))}

          {/* Placeholder — room for more partners */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="rounded-2xl p-8 flex flex-col items-center justify-center min-h-[220px] border border-dashed border-white/15 text-zinc-600 hover:border-[#00F0FF]/40 hover:text-cyan transition-colors"
            data-testid="partner-placeholder"
          >
            <Plus size={28} className="mb-3" />
            <span className="font-mono text-xs uppercase tracking-[0.2em]">{t("partners_more")}</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
